/**
 * נרמול ואיחוד בחירת גמרא לפי מסכתא (טווחים חופפים/רציפים).
 * מקביל ל־backend/src/services/examMaterialSummary.js
 */

/** מיזוג טווחים חופפים או רציפים (ללא פער). */
export function mergeContiguousRanges(ranges) {
  const list = (ranges || [])
    .map((r) => {
      let from = Number(r?.from);
      let to = Number(r?.to);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
      if (from > to) [from, to] = [to, from];
      if (from < 1 || to < 1) return null;
      return { from, to };
    })
    .filter(Boolean)
    .sort((a, b) => a.from - b.from || a.to - b.to);

  if (list.length === 0) return [];

  const merged = [{ from: list[0].from, to: list[0].to }];
  for (let i = 1; i < list.length; i += 1) {
    const cur = list[i];
    const last = merged[merged.length - 1];
    if (cur.from <= last.to + 1) {
      last.to = Math.max(last.to, cur.to);
    } else {
      merged.push({ from: cur.from, to: cur.to });
    }
  }
  return merged;
}

/** ספירת דפים בטווחים: to - from + 1 */
export function countDafimInRanges(ranges) {
  let total = 0;
  for (const rng of ranges || []) {
    const from = Number(rng?.from);
    const to = Number(rng?.to);
    if (Number.isFinite(from) && Number.isFinite(to) && to >= from) {
      total += to - from + 1;
    }
  }
  return total;
}

/**
 * נרמול gemaraSelection: מסכת אחת לכל שם, טווחים חופפים/רציפים מאוחדים.
 * examined נשמר כ־OR אם אותה מסכת מופיעה יותר מפעם.
 */
export function normalizeGemaraSelection(selection) {
  const byTractate = new Map();
  const order = [];

  for (const sel of selection || []) {
    const tractate = String(sel?.tractate || '').trim();
    if (!tractate) continue;
    if (!byTractate.has(tractate)) {
      byTractate.set(tractate, { ranges: [], examined: false });
      order.push(tractate);
    }
    const entry = byTractate.get(tractate);
    if (sel.examined) entry.examined = true;
    for (const r of sel.ranges || []) {
      const from = Number(r?.from);
      const to = Number(r?.to);
      if (!Number.isFinite(from) || !Number.isFinite(to) || from < 1 || to < 1) continue;
      entry.ranges.push({ from, to });
    }
  }

  return order
    .map((tractate) => {
      const entry = byTractate.get(tractate);
      const ranges = mergeContiguousRanges(entry.ranges);
      if (ranges.length === 0) return null;
      return { tractate, ranges, examined: !!entry.examined };
    })
    .filter(Boolean);
}

/** מיזוג בחירת גמרא (מבחן קודם + תוספת) ואז נרמול טווחים. */
export function mergeGemaraSelection(base, extra) {
  const combined = [
    ...(base || []).map((sel) => ({
      tractate: sel.tractate,
      ranges: [...(sel.ranges || [])],
      examined: !!sel.examined,
    })),
    ...(extra || []).map((sel) => ({
      tractate: sel.tractate,
      ranges: [...(sel.ranges || [])],
      examined: !!sel.examined,
    })),
  ];
  return normalizeGemaraSelection(combined);
}

/**
 * סיכום דפים לכל מסכתא + סה״כ.
 * @returns {{ byTractate: { tractate: string, ranges: object[], dafim: number, examined: boolean }[], totalDafim: number }}
 */
export function summarizeGemaraSelection(selection) {
  const normalized = normalizeGemaraSelection(selection);
  const byTractate = normalized.map((sel) => ({
    ...sel,
    dafim: countDafimInRanges(sel.ranges),
  }));
  const totalDafim = byTractate.reduce((sum, s) => sum + s.dafim, 0);
  return { byTractate, totalDafim };
}

/**
 * טקסט תצוגה לעמודת חומר: מסכת (טווחים) · X דף | ...
 * @param {function} toHebrew — המרת מספר לאות עברית (אופציונלי)
 * @param {{ includeGrandTotal?: boolean }} options — האם לצרף «סה״כ Y דף» בסוף (ברירת מחדל: false)
 */
export function formatGemaraSelectionText(selection, toHebrew, options = {}) {
  const { includeGrandTotal = false } = options;
  const { byTractate, totalDafim } = summarizeGemaraSelection(selection);
  if (!byTractate.length) return '';
  const fmt = typeof toHebrew === 'function' ? toHebrew : (n) => String(n);
  const parts = byTractate.map((sel) => {
    const ranges = sel.ranges.map((r) => `${fmt(r.from)}–${fmt(r.to)}`).join(' · ');
    return `${sel.tractate}${ranges ? ` (${ranges})` : ''} · ${sel.dafim} דף`;
  });
  if (includeGrandTotal && totalDafim > 0) {
    parts.push(`סה״כ ${totalDafim} דף`);
  }
  return parts.join(' | ');
}

/** סה״כ דפים מנורמל (בלי כפילויות) לבחירת גמרא */
export function totalDafimInSelection(selection) {
  return summarizeGemaraSelection(selection).totalDafim;
}
